import { useEffect, useRef, type ReactNode } from 'react';
import { useStableId } from '@repletoryreach/sdk-utils/hooks';
import { ariaProps } from '@repletoryreach/sdk-utils/a11y';
import { trapFocus, lockBodyScroll, unlockBodyScroll } from '@repletoryreach/sdk-utils/dom';
import { Portal } from '../../core/Portal/Portal.js';
import { ScrollArea } from '../../scroll/ScrollArea.js';
import { CloseButton } from '../../core/CloseButton/CloseButton.js';
import { Overlay } from '../Overlay/Overlay.js';
import { useReducedMotionMultiplier } from '../../motion/hooks/useReducedMotionMultiplier.js';
import './Drawer.css';

export type DrawerPosition = 'left' | 'right' | 'top' | 'bottom';

export interface DrawerProps {
  opened: boolean;
  onClose: () => void;
  title?: ReactNode;
  position?: DrawerPosition;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | string | number;
  closeOnClickOutside?: boolean;
  closeOnEscape?: boolean;
  withCloseButton?: boolean;
  zIndex?: number;
  portalTarget?: HTMLElement | string;
  overlayProps?: { color?: string; backgroundOpacity?: number; blur?: number };
  children?: ReactNode;
  className?: string;
}

const SIZE_PX: Record<string, string> = {
  xs: '260px', sm: '320px', md: '420px', lg: '560px', xl: '720px'
};

/** Side-anchored drawer with the same focus-trap + body-lock guarantees as Modal. */
export function Drawer({
  opened, onClose,
  title,
  position = 'right',
  size = 'md',
  closeOnClickOutside = true,
  closeOnEscape = true,
  withCloseButton = true,
  zIndex,
  portalTarget,
  overlayProps,
  children,
  className
}: DrawerProps): ReactNode {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const titleId = useStableId('rr-drawer-title');
  const multiplier = useReducedMotionMultiplier();

  useEffect(() => {
    if (!opened) return;
    openerRef.current = (document.activeElement as HTMLElement | null) ?? null;
    lockBodyScroll();
    const cleanupTrap = panelRef.current ? trapFocus(panelRef.current) : () => {};
    return () => { cleanupTrap(); unlockBodyScroll(); openerRef.current?.focus?.(); };
  }, [opened]);

  useEffect(() => {
    if (!opened || !closeOnEscape) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [opened, closeOnEscape, onClose]);

  if (!opened) return null;

  const isVertical = position === 'top' || position === 'bottom';
  const dim = SIZE_PX[size as string] ?? (typeof size === 'number' ? `${size}px` : size);
  const sizeStyle: React.CSSProperties = isVertical ? { height: dim, width: '100%' } : { width: dim, height: '100%' };

  return (
    <Portal target={portalTarget}>
      <div className="rr-drawer-root" style={{ zIndex: zIndex ?? 'var(--rr-z-modal)' as unknown as number }}>
        <Overlay
          color={overlayProps?.color}
          backgroundOpacity={overlayProps?.backgroundOpacity ?? 0.4}
          blur={overlayProps?.blur ?? 4}
          onClick={() => closeOnClickOutside && onClose()}
        />
        <div
          ref={panelRef}
          className={['rr-drawer', `rr-drawer--${position}`, className].filter(Boolean).join(' ')}
          {...ariaProps.dialog({ labelledBy: title ? titleId : undefined })}
          style={{
            ...sizeStyle,
            animationDuration: multiplier === 0 ? '0ms' : undefined
          }}
        >
          {(title || withCloseButton) && (
            <div className="rr-drawer__header">
              {title ? <h2 id={titleId} className="rr-drawer__title">{title}</h2> : <span />}
              {withCloseButton ? <CloseButton onClick={onClose} /> : null}
            </div>
          )}
          <ScrollArea className="rr-drawer__body" viewportClassName="rr-drawer__body-viewport" overscroll="contain">{children}</ScrollArea>
        </div>
      </div>
    </Portal>
  );
}
